import {
  type GqlShareClassInput,
  MeetingParticipantsDocument,
  useCreateMeetingParticipantShareMutation,
  useMeetingParticipantQuery,
  useShareClassesQuery,
} from '../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Button } from '@startuptools/ui/Button';
import { Box } from '@startuptools/ui/Box';
import { Modal } from '@startuptools/ui/Modal';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { LoadingDialog } from '../../../components/simple-dialogs/LoadingDialog/LoadingDialog';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { type FormInputs, VotingListShareForm } from './VotingListShareForm';
import { shareClassToInput } from '../../../helpers/share-class-to-input';
import { ShareholdersMeetingDocument } from '../ShareholdersMeeting.graphql';

export const AddVotingListShare = ({
  meetingParticipantId,
  onClose,
}: {
  meetingParticipantId: string;
  onClose: () => void;
}) => {
  const companyId = useCompanyId();
  const { data, loading, error } = useMeetingParticipantQuery({ variables: { companyId, id: meetingParticipantId } });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate, createQuery] = useCreateMeetingParticipantShareMutation({
    refetchQueries: [ShareholdersMeetingDocument, MeetingParticipantsDocument],
  });
  const {
    data: shareClassesData,
    loading: shareClassesLoading,
    error: shareClassesError,
  } = useShareClassesQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });
  const { control, register, watch, handleSubmit } = useForm<FormInputs>();

  const submit: SubmitHandler<FormInputs> = async data => {
    const selectedShareClass = shareClassesData?.shareClasses.find(sc => sc.name === data.selectedShareClassName);
    let shareClass: GqlShareClassInput;
    if (selectedShareClass) {
      shareClass = { name: selectedShareClass.name, voteWeight: selectedShareClass.voteWeight };
    } else {
      shareClass = { name: data.newShareClassName, voteWeight: data.newShareClassVoteWeight };
    }
    return doCreate({
      variables: {
        companyId,
        input: {
          meetingParticipantId,
          shareClass: shareClassToInput(shareClass),
          numberOfShares: data.numberOfShares,
          numberOfVotes: data.numberOfVotes,
        },
      },
    }).then(() => onClose());
  };

  if (error || shareClassesError) {
    return null;
  }

  if (loading || shareClassesLoading) {
    return <LoadingDialog />;
  }

  if (!data?.meetingParticipant || !shareClassesData) {
    return null;
  }

  const shareClasses = shareClassesData.shareClasses;
  const mp = data.meetingParticipant;

  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <Box paddingY={3}>
          <form onSubmit={handleSubmit(submit)}>
            <Stack gap={3}>
              <Typography level="h4">
                <Trans id="Create voting list entry" />
              </Typography>
              <VotingListShareForm
                watch={watch}
                register={register}
                control={control}
                loading={createQuery.loading}
                meetingParticipant={mp}
                shareClasses={shareClasses}
                defaultShareClassName={shareClasses?.[0]?.name}
              />
              <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
                <Button type="button" onClick={onClose} disabled={createQuery.loading} variant="plain">
                  <Trans id="Cancel" />
                </Button>
                <Button type="submit" loading={createQuery.loading}>
                  <Trans id="Save" />
                </Button>
              </Stack>
            </Stack>
          </form>
        </Box>
      </ModalDialog>
    </Modal>
  );
};
